import React, { useState, useRef, useEffect } from 'react';

/**
 * CategoryTools
 *
 * Category-level admin controls, shared by the Menu and Catering pages.
 * InlineItemEditor covers a single row; these are the same affordances one
 * level up — rename / delete / reorder a whole category, and create a new one.
 *
 * Both hosts are light-surfaced, so the styling lives with the rest of the
 * `.ae-` rules in AdminEditBar.css.
 */

/**
 * "1 item" / "3 items" / "3 categories" — the hints read as counts, so they
 * must not say "1 items" or "3 categorys".
 */
export const countLabel = (n, noun = 'item') => {
  if (n === 1) return `${n} ${noun}`;
  const plural = /[^aeiou]y$/.test(noun) ? `${noun.slice(0, -1)}ies` : `${noun}s`;
  return `${n} ${plural}`;
};

/**
 * True when `name` is already taken (case-insensitively).
 *
 * Category names are identity on the menu page — the nav pill's React key, the
 * expanded/collapsed set and the scroll-to-category ref are all keyed by title
 * — so two categories sharing one name break all three at once. Both the
 * create form and rename refuse a duplicate rather than let that through.
 */
const isTaken = (name, taken = []) =>
  taken.some((t) => t.trim().toLowerCase() === name.trim().toLowerCase());

/**
 * Control strip shown above a category's items while editing.
 *
 * Rendered as its own row rather than inside the page's category header —
 * both hosts make that header a <button>, which can't legally nest these.
 *
 * Props:
 *  title      — current category name
 *  label      — what this level is called ('Category', 'Group', …)
 *  color      — when set, rename also edits a swatch colour (catering groups)
 *  hint       — small right-aligned note (e.g. item count / shape)
 *  onRename   — (name, color) => void. Omit to hide the pencil.
 *  onDelete   — () => void, called after confirmation. Omit to hide the bin.
 *  onMoveUp / onMoveDown — omit to disable that arrow; omit both to hide them.
 */
export const CategoryTitleBar = ({
  title,
  label = 'Category',
  color,
  hint,
  onRename,
  onDelete,
  onMoveUp,
  onMoveDown,
  showMove = true,
  takenNames = [],
}) => {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(title);
  const [draftColor, setDraftColor] = useState(color || '#f59e0b');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(title); }, [title]);
  useEffect(() => { if (color) setDraftColor(color); }, [color]);
  useEffect(() => { if (renaming) inputRef.current?.focus(); }, [renaming]);

  // A rename to a name someone else holds is refused, not silently uniquified.
  const clash = draft.trim() !== title.trim() && isTaken(draft, takenNames);

  const commit = () => {
    const name = draft.trim();
    if (!name || clash) return;
    // Only compare the colour when this level has one — otherwise the default
    // swatch never matches an undefined `color` and every ✓ marks the page
    // dirty, even when nothing was typed.
    const changed = name !== title || (color !== undefined && draftColor !== color);
    if (changed) onRename?.(name, draftColor);
    setRenaming(false);
  };

  const cancel = () => {
    setDraft(title);
    setDraftColor(color || '#f59e0b');
    setRenaming(false);
  };

  const handleDelete = () => {
    if (window.confirm(`Delete the "${title}" ${label.toLowerCase()} and everything in it?`)) {
      onDelete?.();
    }
  };

  if (renaming) {
    return (
      <div className="ae-cat-bar ae-cat-bar--editing">
        <input
          ref={inputRef}
          className="ae-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') cancel();
          }}
          placeholder={`${label} name`}
          aria-label={`${label} name`}
        />
        {color && (
          <input
            type="color"
            className="ae-color"
            value={draftColor}
            onChange={(e) => setDraftColor(e.target.value)}
            title="Label colour"
            aria-label="Label colour"
          />
        )}
        <div className="ae-form__btns">
          <button className="ae-btn-save" onClick={commit} disabled={!draft.trim() || clash}>✓</button>
          <button className="ae-btn-cancel" onClick={cancel}>✕</button>
        </div>
        {clash && <div className="ae-cat-bar__err">“{draft.trim()}” is already taken.</div>}
      </div>
    );
  }

  return (
    <div className="ae-cat-bar">
      <span className="ae-cat-bar__label">{label}</span>
      <span className="ae-cat-bar__name" style={color ? { color } : undefined}>{title}</span>
      {hint && <span className="ae-cat-bar__hint">{hint}</span>}
      <div className="ae-row__btns">
        {showMove && (
          <>
            <button className="ae-icon-btn ae-icon-btn--move" onClick={onMoveUp} disabled={!onMoveUp} title={`Move ${label.toLowerCase()} up`}>↑</button>
            <button className="ae-icon-btn ae-icon-btn--move" onClick={onMoveDown} disabled={!onMoveDown} title={`Move ${label.toLowerCase()} down`}>↓</button>
          </>
        )}
        {onRename && (
          <button className="ae-icon-btn" onClick={() => setRenaming(true)} title={`Rename ${label.toLowerCase()}`}>✏️</button>
        )}
        {onDelete && (
          <button className="ae-icon-btn ae-icon-btn--del" onClick={handleDelete} title={`Delete ${label.toLowerCase()}`}>🗑️</button>
        )}
      </div>
    </div>
  );
};

/**
 * Collapsed "+ Add Category" button that opens a small create form.
 *
 * Props:
 *  label     — button text
 *  heading   — form heading
 *  onCreate  — ({ title, icon, layout, columns }) => void
 *  options   — layout choices [{ id, label, hint, needsColumns }].
 *              A single option renders no picker.
 *  showIcon  — offer the emoji field (menu sections and catering cards use one)
 */
export const AddCategoryPanel = ({
  label = '+ Add Category',
  heading = 'New category',
  namePlaceholder = 'Category name',
  options = [{ id: 'list' }],
  showIcon = true,
  takenNames = [],
  onCreate,
}) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('');
  const [layout, setLayout] = useState(options[0]?.id);
  const [columns, setColumns] = useState('');
  const nameRef = useRef(null);

  useEffect(() => { if (open) nameRef.current?.focus(); }, [open]);

  const opt = options.find((o) => o.id === layout) || options[0];
  const cols = columns.split(',').map((c) => c.trim()).filter(Boolean);
  const clash = !!name.trim() && isTaken(name, takenNames);
  const valid = !!name.trim() && !clash && (!opt?.needsColumns || cols.length > 0);

  const reset = () => {
    setName(''); setIcon(''); setColumns('');
    setLayout(options[0]?.id);
    setOpen(false);
  };

  const submit = () => {
    if (!valid) return;
    onCreate?.({ title: name.trim(), icon: icon.trim(), layout: opt?.id, columns: cols });
    reset();
  };

  if (!open) {
    return (
      <button className="ae-add-btn ae-add-btn--cat" onClick={() => setOpen(true)}>{label}</button>
    );
  }

  return (
    <div className="ae-new-cat">
      <div className="ae-section-title">{heading}</div>
      <div className="ae-new-cat__row">
        {showIcon && (
          <input
            className="ae-input ae-input--icon"
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            placeholder="🍽️"
            aria-label="Category icon"
            maxLength={4}
          />
        )}
        <input
          ref={nameRef}
          className="ae-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !opt?.needsColumns) submit();
            if (e.key === 'Escape') reset();
          }}
          placeholder={namePlaceholder}
          aria-label={namePlaceholder}
        />
      </div>

      {options.length > 1 && (
        <div className="ae-seg" role="group" aria-label="Category layout">
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              aria-pressed={o.id === opt?.id}
              className={`ae-seg__btn${o.id === opt?.id ? ' ae-seg__btn--on' : ''}`}
              onClick={() => setLayout(o.id)}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
      {clash
        ? <div className="ae-new-cat__err">“{name.trim()}” already exists — pick another name.</div>
        : opt?.hint && <div className="ae-new-cat__hint">{opt.hint}</div>}

      {opt?.needsColumns && (
        <input
          className="ae-input"
          value={columns}
          onChange={(e) => setColumns(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') reset();
          }}
          placeholder={opt.columnsPlaceholder || 'Price columns, comma separated — e.g. Dosa, Pesarattu, Ragi Dosa'}
          aria-label="Columns"
        />
      )}

      <div className="ae-new-cat__btns">
        <button className="ae-btn-save" onClick={submit} disabled={!valid}>✓ Create</button>
        <button className="ae-btn-cancel" onClick={reset}>✕ Cancel</button>
      </div>
    </div>
  );
};

/**
 * CategoryOrderPanel — reorder every category from one compact list.
 *
 * Why this exists rather than only the ↑↓ on each card: an item row is 30px
 * tall, so nudging one is immediate and stays on screen. A category card is
 * hundreds of pixels tall, so the same ↑↓ scrolls the button out from under
 * the cursor and moving a category five places means five blind clicks. One
 * row per category puts the whole running order on one screen instead.
 *
 * Dragging uses pointer events, not HTML5 drag-and-drop, because the staff do
 * this on a tablet and HTML5 dragging does not fire on touch. The ↑↓ buttons
 * stay as the keyboard- and screen-reader-accessible path.
 *
 * Props:
 *  items   — [{ id, icon, label, hint }] in current order
 *  onMove  — (fromIndex, toIndex) => void, called once per completed move
 *  onJump  — (id) => void, optional: clicking a row scrolls to that category
 *  title   — panel heading
 */
export const CategoryOrderPanel = ({ items = [], onMove, onJump, title = 'Category order' }) => {
  const [open, setOpen] = useState(false);
  const [drag, setDrag] = useState(null);   // { from, to }
  const listRef = useRef(null);
  const startY = useRef(0);
  const rowStep = useRef(0);

  // Preview the drop while the finger is still down, so the row lands where
  // the list says it will.
  const preview = React.useMemo(() => {
    if (!drag) return items;
    const next = [...items];
    next.splice(drag.to, 0, ...next.splice(drag.from, 1));
    return next;
  }, [items, drag]);

  const beginDrag = (e, index) => {
    const rows = listRef.current?.querySelectorAll('.ae-order__row');
    if (!rows || rows.length < 2) return;                 // nothing to reorder
    rowStep.current = rows[1].getBoundingClientRect().top - rows[0].getBoundingClientRect().top;
    startY.current = e.clientY;
    setDrag({ from: index, to: index });
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  };

  const onDragMove = (e) => {
    if (!drag || !rowStep.current) return;
    const steps = Math.round((e.clientY - startY.current) / rowStep.current);
    const to = Math.min(Math.max(drag.from + steps, 0), items.length - 1);
    if (to !== drag.to) setDrag((d) => ({ ...d, to }));
  };

  const endDrag = () => {
    if (drag && drag.to !== drag.from) onMove?.(drag.from, drag.to);
    setDrag(null);
  };

  // A cancelled pointer (a system gesture, a lost capture) is not a drop —
  // put the row back rather than committing a move nobody asked for.
  const cancelDrag = () => setDrag(null);

  if (!items.length) return null;

  return (
    <div className={`ae-order${open ? ' ae-order--open' : ''}`}>
      <button
        className="ae-order__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="ae-order__toggle-icon">⇅</span>
        <span className="ae-order__toggle-label">{title}</span>
        <span className="ae-order__toggle-hint">{countLabel(items.length, 'category')}</span>
        <span className="ae-order__toggle-arrow">{open ? '−' : '+'}</span>
      </button>

      {open && (
        <>
          <p className="ae-order__help">
            Drag a row, or use ↑ ↓. The menu reorders as you go — publish to keep it.
          </p>
          <ol className="ae-order__list" ref={listRef}>
            {preview.map((item) => {
              const index = items.findIndex((i) => i.id === item.id);
              const dragging = drag && items[drag.from]?.id === item.id;
              return (
                <li
                  key={item.id}
                  className={`ae-order__row${dragging ? ' ae-order__row--dragging' : ''}`}
                >
                  <span
                    className="ae-order__handle"
                    title="Drag to reorder"
                    aria-hidden="true"
                    onPointerDown={(e) => beginDrag(e, index)}
                    onPointerMove={onDragMove}
                    onPointerUp={endDrag}
                    onPointerCancel={cancelDrag}
                  >
                    ⠿
                  </span>
                  <span className="ae-order__icon">{item.icon}</span>
                  <button
                    type="button"
                    className="ae-order__name"
                    onClick={() => onJump?.(item.id)}
                    disabled={!onJump}
                  >
                    {item.label}
                  </button>
                  {item.hint && <span className="ae-order__hint">{item.hint}</span>}
                  <span className="ae-order__btns">
                    <button
                      className="ae-icon-btn ae-icon-btn--move"
                      onClick={() => onMove?.(index, index - 1)}
                      disabled={index === 0}
                      title={`Move ${item.label} up`}
                    >↑</button>
                    <button
                      className="ae-icon-btn ae-icon-btn--move"
                      onClick={() => onMove?.(index, index + 1)}
                      disabled={index === items.length - 1}
                      title={`Move ${item.label} down`}
                    >↓</button>
                  </span>
                </li>
              );
            })}
          </ol>
        </>
      )}
    </div>
  );
};
